import React from "react";
import { Sparkles, ChevronRight } from "lucide-react";
import { levelColor } from "../lib/gamificationApi";
import { Link } from "react-router-dom";

/**
 * Compact level badge — displays current identity, XP, and progress to next.
 *
 * Variants
 * --------
 * `hero`    — full-width card on profile hero (XP bar + tagline)
 * `inline`  — pill-style chip
 * `sidebar` — vertical card for sidebars
 */
export default function LevelBadge({ levelData, variant = "inline", linkable = true, testIdPrefix = "level" }) {
    if (!levelData) return null;
    const { slug, name, xp, xp_to_next, progress_pct, next_level, tagline } = levelData;
    const color = levelColor(slug);

    const innerLink = (children) => (linkable
        ? <Link to="/passport" className="block group">{children}</Link>
        : <div>{children}</div>);

    if (variant === "inline") {
        return innerLink(
            <span data-testid={`${testIdPrefix}-inline`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest text-white shadow-sm" style={{ backgroundColor: color }}>
                <Sparkles className="w-3 h-3" />
                {name}
            </span>
        );
    }

    if (variant === "hero") {
        return innerLink(
            <div data-testid={`${testIdPrefix}-hero`} className="bg-snowcap border border-ridge-line rounded-xl p-4 hover:border-ember-orange transition-all">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: color }}>
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">Adventure Level</div>
                        <div className="font-display font-black text-lg text-midnight-pine truncate leading-tight">{name}</div>
                    </div>
                    {linkable && <ChevronRight className="w-4 h-4 text-slate-gray group-hover:text-ember-orange" />}
                </div>
                <div className="h-2 bg-trail-tan/60 rounded-full overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${progress_pct}%`, backgroundColor: color }} />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-xs">
                    <span className="font-bold text-granite">{xp.toLocaleString()} XP</span>
                    {next_level
                        ? <span className="text-slate-gray">{xp_to_next.toLocaleString()} to {next_level.name}</span>
                        : <span className="text-ember-orange font-bold">Top tier reached</span>
                    }
                </div>
                {tagline && <p className="text-xs text-slate-gray italic mt-2">"{tagline}"</p>}
            </div>
        );
    }

    // sidebar
    return innerLink(
        <div data-testid={`${testIdPrefix}-sidebar`} className="bg-snowcap border border-ridge-line rounded-xl p-5 hover:border-ember-orange transition-all">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" style={{ color }} />
                <h3 className="font-display font-bold text-base text-midnight-pine">Level</h3>
            </div>
            <div className="font-display font-black text-2xl tracking-tight" style={{ color }}>{name}</div>
            <div className="text-xs text-slate-gray mt-1">{xp.toLocaleString()} XP</div>
            <div className="h-2 bg-trail-tan/60 rounded-full overflow-hidden mt-3">
                <div className="h-full transition-all" style={{ width: `${progress_pct}%`, backgroundColor: color }} />
            </div>
            {next_level && <div className="text-[10px] uppercase tracking-widest font-bold text-slate-gray mt-2">{xp_to_next.toLocaleString()} XP to {next_level.name}</div>}
        </div>
    );
}
